import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Download, Share2, Trophy, Loader2 } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { AutoHide } from "@/lib/useHideAfter";
import {
  loadUserContext,
  computeAchievements,
  type Achievement,
  type UserContext,
} from "@/lib/viral/victoryAchievements";
import {
  renderVictoryCard,
  downloadDataUrl,
  dataUrlToFile,
} from "@/lib/viral/victoryCardRenderer";
import { recordShare, type SharePlatform } from "@/lib/viral/victoryShareTracking";
import { buildCanonicalShareUrl } from "@/lib/share/canonicalShare";
import { PREVIEW_MODE } from "@/lib/previewMode";
import { OFFICIAL_MATCH } from "@/lib/officialMatch";

/**
 * PREVIEW MODE — Static showcase deck pinned to OFFICIAL_MATCH.
 * Rendered when the visitor is signed-out or has no achievements yet
 * so the Viral Cards Hub never sits empty during the Final preview.
 */
const PREVIEW_SHOWCASE: Array<{
  id: string;
  emoji: string;
  headline: string;
  subline: string;
  gradient: string;
}> = [
  { id: "pv_goal", emoji: "⚽", headline: "¡GOOOOL!", subline: `${OFFICIAL_MATCH.homeName} 2 – 1 ${OFFICIAL_MATCH.awayName}`, gradient: "from-amber-500 via-red-600 to-black" },
  { id: "pv_victory_esp", emoji: "🏆", headline: "¡Somos Campeones!", subline: `${OFFICIAL_MATCH.homeFlag} Orgullo Español`, gradient: "from-red-600 via-yellow-500 to-red-700" },
  { id: "pv_victory_arg", emoji: "🏆", headline: "¡Vamos Argentina!", subline: `${OFFICIAL_MATCH.awayFlag} La Albiceleste`, gradient: "from-sky-400 via-white to-sky-600" },
  { id: "pv_prediction", emoji: "🎯", headline: "Yo acerté el marcador", subline: "Predicción exacta · +50 XP", gradient: "from-emerald-500 via-emerald-700 to-black" },
  { id: "pv_mvp", emoji: "👑", headline: "MVP del partido", subline: "El fan lo eligió en vivo", gradient: "from-amber-400 via-orange-500 to-red-700" },
  { id: "pv_debate", emoji: "⚖️", headline: "¿Fue penalti?", subline: "Sí · No — vota con tu nación", gradient: "from-violet-500 via-fuchsia-600 to-indigo-900" },
];

function PreviewShowcase() {
  const onShare = (card: (typeof PREVIEW_SHOWCASE)[number]) => {
    const url = "https://versesport.com/";
    const text = `${card.emoji} ${card.headline} — ${OFFICIAL_MATCH.competition} ${OFFICIAL_MATCH.homeFlag}${OFFICIAL_MATCH.awayFlag} · VerseSport™ #Mundial`;
    const nav = navigator as Navigator & { share?: (d: ShareData) => Promise<void> };
    if (nav.share) {
      void nav.share({ title: "VerseSport", text, url }).catch(() => {});
    } else {
      window.open(`https://wa.me/?text=${encodeURIComponent(`${text} ${url}`)}`, "_blank", "noopener,noreferrer");
    }
  };
  return (
    <div className="grid gap-4 md:grid-cols-2">
      {PREVIEW_SHOWCASE.map((c) => (
        <Card key={c.id} className="overflow-hidden">
          <CardContent className="p-3 space-y-3">
            <div className={`relative aspect-[9/16] overflow-hidden rounded-xl bg-gradient-to-br ${c.gradient}`}>
              <div
                aria-hidden
                className="pointer-events-none absolute inset-0 opacity-40 mix-blend-overlay"
                style={{ background: "linear-gradient(120deg, rgba(255,255,255,0.4) 0%, transparent 35%, transparent 60%, rgba(255,255,255,0.25) 100%)" }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-4 text-white">
                <div className="text-4xl drop-shadow">{c.emoji}</div>
                <div className="mt-1 text-[10px] uppercase tracking-[0.2em] opacity-80">{OFFICIAL_MATCH.competition}</div>
                <div className="text-lg font-black leading-tight drop-shadow-md">{c.headline}</div>
                <div className="text-xs opacity-90">{c.subline}</div>
                <div className="mt-2 flex items-center justify-between text-[9px] font-black uppercase tracking-widest">
                  <span className="opacity-80">VerseSport™</span>
                  <span className="rounded-full bg-white/15 px-1.5 py-0.5 backdrop-blur">
                    {OFFICIAL_MATCH.homeFlag} vs {OFFICIAL_MATCH.awayFlag}
                  </span>
                </div>
              </div>
            </div>
            <Button size="sm" className="w-full" onClick={() => onShare(c)}>
              <Share2 className="h-4 w-4 mr-1" /> Compartir
            </Button>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

/**
 * Viral Victory Cards Hub — generates real, share-ready 1080x1920 cards
 * for the signed-in user, with native share / WhatsApp / download fallback.
 */
export function VictoryCardsHub() {
  const [ctx, setCtx] = useState<UserContext | null>(null);
  const [loading, setLoading] = useState(true);
  const [previews, setPreviews] = useState<Record<string, string>>({});
  const [sharing, setSharing] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const u = await loadUserContext();
        setCtx(u);
      } catch (e) {
        console.warn("[victory] loadUserContext failed", e);
        setCtx(null);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const achievements = useMemo(() => (ctx ? computeAchievements(ctx) : []), [ctx]);

  useEffect(() => {
    if (!ctx || achievements.length === 0) return;
    let cancelled = false;
    void (async () => {
      const next: Record<string, string> = {};
      for (const ach of achievements) {
        try {
          next[ach.id] = await renderVictoryCard(ach, ctx);
        } catch (e) {
          console.warn("[victory] render failed", ach.id, e);
        }
        if (cancelled) return;
      }
      if (!cancelled) setPreviews(next);
    })();
    return () => { cancelled = true; };
  }, [ctx, achievements]);

  async function handleShare(ach: Achievement, platform: SharePlatform) {
    if (!ctx) return;
    setSharing(`${ach.id}:${platform}`);
    try {
      const dataUrl = previews[ach.id] ?? (await renderVictoryCard(ach, ctx));
      const filename = `versesport-${ach.type}-${Date.now()}.png`;
      const NATION_TAGS: Record<string, string> = {
        MX: "#Mexico #FMF", MEX: "#Mexico #FMF",
        AR: "#Argentina #Albiceleste", ARG: "#Argentina #Albiceleste",
        BR: "#Brasil #Selecao", BRA: "#Brasil #Selecao",
        CO: "#Colombia #VamosColombia", COL: "#Colombia #VamosColombia",
        EC: "#Ecuador #LaTri", ECU: "#Ecuador #LaTri",
        US: "#USMNT", USA: "#USMNT",
        UY: "#Uruguay #Celeste", URY: "#Uruguay #Celeste",
        CL: "#Chile #LaRoja", CHL: "#Chile #LaRoja",
        PE: "#Peru #ArribaPeru", PER: "#Peru #ArribaPeru",
        ES: "#España #LaRoja", ESP: "#España #LaRoja",
        PT: "#Portugal #SelecaoPT", POR: "#Portugal #SelecaoPT",
        FR: "#France #LesBleus", FRA: "#France #LesBleus",
        DE: "#Germany #DieMannschaft", GER: "#Germany #DieMannschaft",
        IT: "#Italia #Azzurri", ITA: "#Italia #Azzurri",
      };
      const nationTag = ctx.nation_code ? NATION_TAGS[ctx.nation_code.toUpperCase()] ?? `#${ctx.nation_code.toUpperCase()}` : "";
      const hashtags = ["#Mundial2026", "#VerseSport", nationTag].filter(Boolean).join(" ");
      const text = `${ach.emoji} ${ach.headline}${ach.subline ? " · " + ach.subline : ""} — VerseSport ${hashtags}`;
      const url = buildCanonicalShareUrl("/", { source: "victory_card", medium: platform, campaign: ach.type });

      if (platform === "native_share") {
        const file = await dataUrlToFile(dataUrl, filename);
        const nav = navigator as Navigator & {
          canShare?: (d: { files?: File[] }) => boolean;
          share?: (d: ShareData & { files?: File[] }) => Promise<void>;
        };
        if (nav.canShare?.({ files: [file] }) && nav.share) {
          await nav.share({ files: [file], title: "VerseSport", text, url });
        } else {
          downloadDataUrl(dataUrl, filename);
        }
      } else if (platform === "whatsapp") {
        downloadDataUrl(dataUrl, filename);
        window.open(`https://wa.me/?text=${encodeURIComponent(`${text} ${url}`)}`, "_blank", "noopener,noreferrer");
      } else if (platform === "instagram" || platform === "tiktok") {
        // No public web intent that accepts an image directly — download and prompt user to upload.
        downloadDataUrl(dataUrl, filename);
      } else if (platform === "download") {
        downloadDataUrl(dataUrl, filename);
      }
      await recordShare({ achievement: ach, platform, nationCode: ctx.nation_code });
    } catch (e) {
      console.warn("[victory] share failed", e);
    } finally {
      setSharing(null);
    }
  }

  if (loading) {
    return (
      <AutoHide ms={2000}>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Cargando logros…
        </div>
      </AutoHide>
    );
  }

  if (!ctx) {
    if (PREVIEW_MODE) return <PreviewShowcase />;
    return (
      <Card className="border-dashed">
        <CardContent className="p-8 text-center space-y-2">
          <Trophy className="h-10 w-10 mx-auto text-muted-foreground" />
          <p className="text-base font-medium">Inicia sesión para desbloquear tus Victory Cards.</p>
        </CardContent>
      </Card>
    );
  }

  if (achievements.length === 0) {
    if (PREVIEW_MODE) return <PreviewShowcase />;
    return (
      <Card className="border-dashed">
        <CardContent className="p-8 text-center space-y-3">
          <Trophy className="h-10 w-10 mx-auto text-primary" />
          <p className="text-base font-bold">Tu historia mundialista está por comenzar.</p>
          <p className="text-sm text-muted-foreground max-w-md mx-auto">
            Participa en predicciones. Gana XP. Representa a tu nación.
            Desbloquea tarjetas épicas para compartir.
          </p>
          <Button asChild size="sm" className="mt-2">
            <Link to="/">Ir al Match Viewer</Link>
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid gap-4 md:grid-cols-2">
      {achievements.map((ach) => (
        <Card key={ach.id} className="overflow-hidden">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <span className="text-xl">{ach.emoji}</span>
              <span className="truncate">{ach.headline}</span>
              <Badge variant="outline" className="ml-auto text-[10px] uppercase">{ach.type.replace(/_/g, " ")}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="rounded-lg border border-border/60 bg-black/40 p-2">
              {previews[ach.id] ? (
                <img src={previews[ach.id]} alt={ach.headline} className="w-full rounded-md" />
              ) : (
                <div className="aspect-[9/16] flex items-center justify-center text-xs text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin mr-1" /> Generando tarjeta…
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Button size="sm" onClick={() => handleShare(ach, "native_share")} disabled={!!sharing}>
                <Share2 className="h-4 w-4 mr-1" /> Compartir
              </Button>
              <Button size="sm" variant="secondary" onClick={() => handleShare(ach, "whatsapp")} disabled={!!sharing}>
                WhatsApp
              </Button>
              <Button size="sm" variant="secondary" onClick={() => handleShare(ach, "instagram")} disabled={!!sharing}>
                Instagram
              </Button>
              <Button size="sm" variant="secondary" onClick={() => handleShare(ach, "tiktok")} disabled={!!sharing}>
                TikTok
              </Button>
              <Button size="sm" variant="outline" className="col-span-2" onClick={() => handleShare(ach, "download")} disabled={!!sharing}>
                <Download className="h-4 w-4 mr-1" /> Descargar PNG
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
